import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/App";
import axios from "axios";
import {
  Wrench,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  Building2,
  CheckCircle2,
  AlertTriangle,
  FileText,
  DollarSign,
  Clock,
  Briefcase
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ForgotPasswordModal from "@/components/ForgotPasswordModal";
import ForcedPasswordChangeModal from "@/components/ForcedPasswordChangeModal";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_isaac-platform/artifacts/9qsxumik_Sterile%20Mate%20Logo%20%282%29.jpg";

export default function ContractorLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [pendingContractorId, setPendingContractorId] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  // Forced password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [contractorName, setContractorName] = useState("");

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem("contractor_token");
    if (token) {
      navigate("/contractor/dashboard");
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }

    setLoading(true);
    try {
      // Try the new temp password auth endpoint first, fallback to old endpoint
      let res;
      try {
        res = await axios.post(`${API}/auth/contractor/login`, { email, password });
      } catch (newEndpointError) {
        // Fallback to old endpoint if new one doesn't exist
        res = await axios.post(`${API}/contractor/login`, { email, password });
      }
      
      // Check if password change is required (temp password)
      if (res.data.force_password_change) {
        setTempToken(res.data.token);
        setContractorName(res.data.contractor?.name || "");
        // Store contractor data for after password change
        localStorage.setItem("contractor_data", JSON.stringify(res.data.contractor));
        setShowPasswordChange(true);
        setLoading(false);
        return;
      }
      
      localStorage.setItem("contractor_token", res.data.token);
      localStorage.setItem("contractor_data", JSON.stringify(res.data.contractor));
      toast.success("Login successful");
      navigate("/contractor/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail || "Login failed";
      
      // Check if terms need to be signed
      if (err.response?.status === 403 && detail.includes("Terms")) {
        setPendingContractorId(email);
        setShowTerms(true);
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handlePasswordChangeSuccess = (newToken) => {
    // Password changed, save token and redirect
    localStorage.setItem("contractor_token", newToken);
    setShowPasswordChange(false);
    toast.success("Password changed! Welcome to your dashboard.");
    // Use window.location.replace for more reliable redirect
    setTimeout(() => {
      window.location.replace("/contractor/dashboard");
    }, 100);
  };

  const acceptTerms = async () => {
    try {
      // Note: In production, you'd get the contractor ID from a secure flow
      toast.success("Terms accepted. Please login again.");
      setShowTerms(false);
    } catch (err) {
      toast.error("Failed to accept terms");
    }
  };

  if (showTerms) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full overflow-hidden">
          <div className="bg-blue-900 text-white p-6">
            <h1 className="text-2xl font-bold">Contractor Terms of Service</h1>
            <p className="text-blue-200 mt-1">Please read and accept to continue</p>
          </div>
          <div className="p-6 max-h-96 overflow-y-auto">
            <div className="prose prose-sm">
              <h3>AskIsaac Inc. - Contractor Agreement</h3>
              <p>By accepting these terms, you agree to:</p>
              <ol className="space-y-2 text-sm text-slate-600">
                <li><strong>Service Standards:</strong> Perform all work in accordance with industry standards and manufacturer specifications.</li>
                <li><strong>Insurance Requirements:</strong> Maintain valid Certificate of Insurance (COI) with minimum $1M liability coverage.</li>
                <li><strong>Licensing:</strong> Keep all required licenses current and valid for your service area.</li>
                <li><strong>Response Times:</strong> Acknowledge job dispatches within 2 hours and provide ETAs promptly.</li>
                <li><strong>Warranty Compliance:</strong> Honor all warranty restrictions - no charges for warranty-covered items without authorization.</li>
                <li><strong>Documentation:</strong> Provide completion photos and obtain hospital sign-off for all work.</li>
                <li><strong>Confidentiality:</strong> Maintain confidentiality of all hospital and patient-related information.</li>
                <li><strong>Payment Terms:</strong> Submit accurate invoices; payment processed within 30 days of approval.</li>
                <li><strong>Non-Solicitation:</strong> Do not directly solicit business from Sterile Mate hospital partners.</li>
                <li><strong>Termination:</strong> Either party may terminate with 30 days written notice.</li>
              </ol>
            </div>
          </div>
          <div className="p-6 bg-slate-50 border-t border-slate-200 flex justify-between items-center">
            <Button variant="outline" onClick={() => setShowTerms(false)}>
              Cancel
            </Button>
            <Button onClick={acceptTerms} className="bg-blue-700 hover:bg-blue-800">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              I Accept These Terms
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="contractor-login-page">
      {/* ============ LEFT — HERO TECHNICIAN PHOTO ============ */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="/api/static/email_assets/login_contractor_technician.jpg"
          alt="Sterile Mate skilled-trade contractor technician"
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1a35]/72 via-[#0a1a35]/45 to-[#0056A8]/60" />
        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full text-white">
          <div>
            <Link to="/" className="inline-flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-900/40">
                <Wrench className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-cyan-200">
                  Sterile Mate · Powered by AskIsaac
                </p>
                <p className="text-sm text-white/80">Contractor / Skilled-Trades Portal</p>
              </div>
            </Link>
          </div>

          <div className="max-w-lg">
            <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-cyan-300 mb-4">
              Welcome back
            </p>
            <h2 className="text-4xl xl:text-5xl font-black leading-[1.05] mb-5 drop-shadow-xl">
              Job in. Job done.<br />Invoice paid.
            </h2>
            <p className="text-lg text-white/90 leading-relaxed drop-shadow max-w-md mb-6">
              Sign in to accept dispatches, track service jobs in real time, submit photos and
              invoices, and pull your payment history — DI-tank swaps, water systems, general
              maintenance, and capital projects.
            </p>

            <div className="grid grid-cols-2 gap-3 max-w-md">
              {[
                { icon: Briefcase, text: "Accept & schedule jobs" },
                { icon: Clock, text: "Real-time status updates" },
                { icon: DollarSign, text: "Fast invoice submission" },
                { icon: FileText, text: "1099 &amp; payment history" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2.5 bg-white/10 backdrop-blur border border-white/15 rounded-lg px-3 py-2.5">
                  <item.icon className="w-4 h-4 text-cyan-300 shrink-0" />
                  <span className="text-xs text-white/90 leading-tight">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-6 text-xs text-white/70">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              COI verified
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
              License current
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              W-9 on file
            </span>
          </div>
        </div>
      </div>

      {/* ============ RIGHT — LOGIN FORM ============ */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 text-center">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-12 h-12 bg-blue-700 rounded-xl flex items-center justify-center shadow">
                <Wrench className="w-6 h-6 text-white" />
              </div>
              <p className="text-lg font-black text-slate-900">Contractor Portal</p>
            </Link>
          </div>

          <div>
            <h1 className="text-3xl font-black text-slate-900 mb-2">Contractor Login</h1>
            <p className="text-slate-600">Access your Sterile Mate service portal.</p>
          </div>

          <div className="mt-8 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-blue-100 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center shadow-sm">
                  <Wrench className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Skilled-Trades Portal</p>
                  <p className="text-sm text-slate-500">For approved contractors</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleLogin} className="p-6 space-y-5">
              <div>
                <Label className="text-slate-700">Email Address</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@company.com"
                    className="pl-10 border-slate-200"
                    autoFocus
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-700">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 pr-10 border-slate-200"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-xs text-blue-600 hover:underline mt-2"
                  data-testid="contractor-forgot-password"
                >
                  Forgot password?
                </button>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-700 hover:bg-blue-800 text-white h-11 text-sm font-semibold"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing in...</>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>

            {/* First-time user callout */}
            <div className="px-6 pb-5 pt-1">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-amber-800 mb-1.5 inline-flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  New contractor?
                </p>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Your account must be activated by a Sterile Mate administrator before you can
                  log in.{" "}
                  <Link to="/register?type=contractor" className="font-semibold text-blue-700 hover:underline">
                    Apply here
                  </Link>{" "}
                  and upload your current COI, license, and W-9 — we typically approve within 24
                  hours.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center space-y-2">
            <Link to="/" className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
              ← Back to main site
            </Link>
            <p className="text-slate-500 text-sm">
              Support · <a href="tel:8009883892" className="text-blue-700 hover:underline font-medium">800.988.3892</a>
            </p>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        open={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        userType="contractor"
        title="Reset Contractor Password"
      />
      
      {/* Forced Password Change Modal (for temp passwords) */}
      <ForcedPasswordChangeModal
        open={showPasswordChange}
        token={tempToken}
        userType="contractor"
        userName={contractorName}
        onSuccess={handlePasswordChangeSuccess}
      />
    </div>
  );
}
